import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import _ from "lodash";
import FilterGroup from "../../common/reports/FilterGroup";
import FilterItem from "../../common/reports/FilterItem";
import Filters from "../../common/reports/Filters";
import { LIKE } from "../../common/reports/reportConstants";
import { createFilterParam } from "../../common/reports/reportUtils";
import Button from "../../framework/Button";
import GlobalContext from "../../framework/GlobalContext";
import NumberInput from "../../framework/NumberInput";
import SelectInput from "../../framework/SelectInput";
import Shortcut from "../../framework/Shortcut";
import TextInput from "../../framework/TextInput";
import { isEmpty } from "../../utils/utils";
import { getDoctors} from "../../services/doctor-service";
import { doctorTypes } from "../../utils/constant";

const DoctorReportFilter = ({
  reportRef,
  onFilterShow,
  refreshData = false,
  applyFilters = () => {},
  applyFilterOnMount = true,
}) => {
  const [doctorOptions, setDoctorOptions] = useState([]);
  const [doctor, setDoctor] = useState("");
  const [hospital, sethospital] = useState("");
  const [qualification, setQualification] = useState("");
  const [pincode, setPincode] = useState();
  const [licenceNo, setLicenceNo] = useState("");
  const [speciality, setSpeciality] = useState("");
  const [doctorType, setDoctorType] = useState("");
  const [orderBy] = useState([{ fieldName: "createdTime", orderType: "desc" }]);

  const apr_filter_doctor_name_ref = useRef();
  const applyFilterRef = useRef();
  const { setCurrentComponent } = useContext(GlobalContext);
  const withPincodeCap = ({ value }) => value.length <= 6;
  const applyFilterHandler = useCallback(
    (isFilterChanged = false) => {
      let filters = [];
      let result = {};
      if (!isEmpty(doctor)) {
        filters.push(
          createFilterParam("doctorName", `${doctor}`, "string", LIKE)
        );
      }
      if (!isEmpty(hospital)) {
        filters.push(
          createFilterParam("doctorHospital", `${hospital}`, "string", LIKE)
        );
      }
      if (!isEmpty(qualification)) {
        filters.push(
          createFilterParam("qualification", `${qualification}`, "string", LIKE)
        );
      }
      if (!isEmpty(licenceNo)) {
        filters.push(
          createFilterParam("licenceNo", `${licenceNo}`, "string", LIKE)
        );
      }
      if (!isEmpty(speciality)) {
        filters.push(
          createFilterParam("speciality", `${speciality}`, "string", LIKE)
        );
      }
      if (!isEmpty(doctorType)) {
        filters.push(
          createFilterParam("doctorType", `${doctorType.id}`, "string", LIKE)
        );
      }
      if (!isEmpty(pincode)) {
        filters.push(
          createFilterParam("pincode", `${pincode}`, "string", LIKE)
        );
      }
      if (isFilterChanged) {
        result.filters = filters;
      }
      if (orderBy) {
        result.orderBy = orderBy;
      }
      applyFilters(result, {
        doctor,
        pincode,
        qualification,
        orderBy,
        doctorType,
      });
      reportRef.current?.applyFilters(result);
    },
    [
      applyFilters,
      pincode,
      orderBy,
      doctor,
      hospital,
      licenceNo,
      speciality,
      qualification,
      reportRef,
      doctorType,
    ]
  );
  let debounce = useCallback(
    _.debounce(() => {
      applyFilterHandler();
    }, 300),
    []
  );
  const filterListener = useCallback((event) => {
    const key = event.key.toLowerCase();
    const ctrl = event.ctrlKey;
    if (ctrl && key === "enter") {
      return applyFilterRef.current.click();
    }
  }, []);

  const filterContent = (
    <>
      <FilterGroup>
        <FilterItem>
          <SelectInput
            id="apr_filter_doctor_name"
            label="Doctor Name"
            options={doctorOptions}
            isOptionEqualToValue={(option, value) => {
              return option.label === value.label || value === "";
            }}
            ref={apr_filter_doctor_name_ref}
            getOptionLabel={(option) => option.label || option}
            onFocus={() => setCurrentComponent("apr_filter_doctor_name")}
            onChange={(e, values) => {
              setDoctor(values.label);
            }}
            freeSolo
            value={doctor}
            onInputChange={(e) => {
              if (!e) return;
              setDoctor(e.target.value);
              setDoctorOptions([]);
              if (!e?.target?.value) setDoctor(null);
              if (e?.target?.value?.length > 2) {
                getDoctors({
                  orderBy: [{ fieldName: "doctorName", orderType: "asc" }],
                  filters: [
                    {
                      fieldName: "doctorName",
                      fieldType: { type: "string", operation: LIKE },
                      fieldValue: e?.target?.value,
                    },
                  ],
                  includeFields: ["doctorName", "doctorId"],
                }).then(
                  (resp) => {
                    let updatedResp = [];
                    resp.forEach((prod) => {
                      updatedResp.push({
                        id: prod.doctorId,
                        label: prod.doctorName,
                      });
                    });
                    setDoctorOptions(updatedResp);
                  }
                );
              }
            }}
            markers={JSON.stringify({
              up: "apr_filter_doctor_name",
              down: "apr_filter_hospital_name",
              right: "apr_filter_hospital_name",
              left: "apr_filter_doctor_name",
            })}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <TextInput
            id="apr_filter_hospital_name"
            label="Hospital Name"
            onFocus={() => setCurrentComponent("apr_filter_hospital_name")}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "apr_filter_doctor_name",
                down: "apr_filter_qualification",
                right: "apr_filter_qualification",
                left: "apr_filter_doctor_name",
              }),
            }}
            value={hospital}
            onChange={(e) => {
              sethospital(e.target.value);
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <TextInput
            id="apr_filter_qualification"
            label="Qualification"
            onFocus={() => setCurrentComponent("apr_filter_qualification")}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "apr_filter_hospital_name",
                down: "apr_filter_speciality",
                right: "apr_filter_speciality",
                left: "apr_filter_hospital_name",
              }),
            }}
            value={qualification}
            onChange={(e) => {
              setQualification(e.target.value);
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <TextInput
            id="apr_filter_speciality"
            label="Speciality"
            onFocus={() => setCurrentComponent("apr_filter_speciality")}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "apr_filter_qualification",
                down: "apr_filter_doctor_type",
                right: "apr_filter_doctor_type",
                left: "apr_filter_qualification",
              }),
            }}
            value={speciality}
            onChange={(e) => {
              setSpeciality(e.target.value);
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <SelectInput
            id="apr_filter_doctor_type"
            label="Doctor Type"
            onFocus={() => setCurrentComponent("apr_filter_doctor_type")}
            options={doctorTypes}
            getOptionLabel={(option) => option.label || option}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disableClearable={false}
            markers={JSON.stringify({
                up: "apr_filter_speciality",
                down: "apr_filter_licence_number",
                right: "apr_filter_licence_number",
                left: "apr_filter_speciality",
              })}
            value={doctorType}
            onChange={(e, option) => {
              setDoctorType(option);
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <TextInput
            id="apr_filter_licence_number"
            label="License Number"
            onFocus={() => setCurrentComponent("apr_filter_licence_number")}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "apr_filter_doctor_type",
                down: "apr_filter_pincode",
                right: "apr_filter_pincode",
                left: "apr_filter_doctor_type",
              }),
            }}
            value={licenceNo}
            onChange={(e) => {
              setLicenceNo(e.target.value);
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <NumberInput
            label="Pincode"
            id="apr_filter_pincode"
            value={pincode}
            onFocus={() => setCurrentComponent("apr_filter_pincode")}
            onValueChange={(values, e) => {
              setPincode(values.floatValue);
            }}
            isAllowed={withPincodeCap}
            decimalScale={0}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "apr_filter_licence_number",
                left: "apr_filter_licence_number",
                down: "apr_apply_filter_button",
                right: "apr_apply_filter_button",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
    </>
  );
  const filterFooter = (
    <>
      <FilterGroup>
        <FilterItem>
          <Button
            id="apr_apply_filter_button"
            label="Apply"
            ref={applyFilterRef}
            endIcon={<Shortcut keys={["Ctrl", "Enter"]} />}
            onClick={() => {
              applyFilterHandler(true);
            }}
            markers={JSON.stringify({
              up: "apr_filter_pincode",
              left: "apr_filter_pincode",
              right: "apr_apply_filter_button",
              down: "apr_apply_filter_button",
            })}
          />
        </FilterItem>
      </FilterGroup>
    </>
  );

  useEffect(() => {
    if (refreshData) {
      applyFilterHandler(true);
    }
  }, [refreshData, applyFilterHandler]);
  useEffect(() => {
    if (onFilterShow) {
      apr_filter_doctor_name_ref?.current.focus({ preventScroll: true });
    }
  }, [onFilterShow]);
  useEffect(() => {
    if (onFilterShow) document.addEventListener("keyup", filterListener, false);
    return () => {
      document.removeEventListener("keyup", filterListener, false);
    };
  }, [filterListener, onFilterShow]);
  useEffect(() => {
    if (applyFilterOnMount) debounce();
    return () => {
      debounce.cancel();
    };
  }, [applyFilterOnMount, debounce]);

  return <Filters filterContent={filterContent} filterFooter={filterFooter} />;
};

export default DoctorReportFilter;
